import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ArrowLeft, Lock, Loader2, LogOut, KeyRound } from "lucide-react";
import {
  adminStats, getDomains, adminCreateDomain, adminUpdateDomain,
  adminDeleteDomain, adminBookings, adminUpdateBooking,
  adminUsers, adminResetUserPassword, adminDeleteUser, adminDeleteBooking,
  getSettings, adminUpdateSettings, formatUSD,
} from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

function AdminGate() {
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(form);
      if (u.role !== "admin") setErr("This account does not have admin access.");
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Invalid credentials.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-gate">
      <div className="max-w-sm w-full bg-[var(--panel-soft)] border border-line rounded-2xl p-9 backdrop-blur-md">
        <div className="w-14 h-14 rounded-full border border-gold/30 flex items-center justify-center mb-6">
          <Lock size={20} className="text-gold" />
        </div>
        <div className="overline mb-2">Restricted</div>
        <h1 className="font-serif text-4xl text-ink mb-2">Admin sign in</h1>
        <p className="text-ink-dim text-sm mb-7">This area is limited to elite-domains.com administrators.</p>
        <form onSubmit={submit} className="space-y-3" data-testid="admin-login-form">
          <input className="w-full bg-[var(--field-bg-soft)] border border-line rounded-lg px-4 py-3 text-sm text-ink focus:border-gold focus:outline-none placeholder:text-ink-dim" type="email" placeholder="Admin email" value={form.email} onChange={set("email")} data-testid="admin-email" required />
          <input className="w-full bg-[var(--field-bg-soft)] border border-line rounded-lg px-4 py-3 text-sm text-ink focus:border-gold focus:outline-none placeholder:text-ink-dim" type="password" placeholder="Password" value={form.password} onChange={set("password")} data-testid="admin-password" required />
          {err && <p className="text-red-400 text-sm" data-testid="admin-login-error">{err}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full bg-gold text-bg font-sans font-bold text-sm py-3.5 rounded-lg hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] transition-shadow flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 size={15} className="animate-spin" />} Sign in
          </button>
        </form>
        <Link to="/" data-testid="admin-gate-home" className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink-dim hover:text-ink transition-colors">
          <ArrowLeft size={13} /> Back to site
        </Link>
      </div>
    </div>
  );
}

const field = "w-full bg-[var(--field-bg)] border border-line text-ink font-sans text-sm px-4 py-2.5 focus:border-gold focus:outline-none transition-colors placeholder:text-ink-dim";
const empty = { name: "", category: "Premium", price: "On Request", status: "available", tagline: "", description: "", featured: false };

function DomainForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name) return toast.error("Domain name is required.");
    setSaving(true);
    try {
      if (initial?.id) await adminUpdateDomain(initial.id, form);
      else await adminCreateDomain(form);
      toast.success(initial?.id ? "Domain updated." : "Domain added.");
      onSaved();
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="domain-form-modal">
      <div className="bg-surface border border-gold/25 w-full max-w-lg p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-serif text-3xl text-ink">{initial?.id ? "Edit Domain" : "New Domain"}</h3>
          <button onClick={onClose} data-testid="close-form"><X className="text-ink-dim hover:text-ink" /></button>
        </div>
        <div className="space-y-3">
          <input className={field} placeholder="Domain name (e.g. Vault.com)" value={form.name} onChange={set("name")} data-testid="form-name" />
          <div className="grid grid-cols-2 gap-3">
            <input className={field} placeholder="Category" value={form.category} onChange={set("category")} data-testid="form-category" />
            <input className={field} placeholder="Price" value={form.price} onChange={set("price")} data-testid="form-price" />
          </div>
          <input className={field} placeholder="Tagline" value={form.tagline} onChange={set("tagline")} data-testid="form-tagline" />
          <textarea className={`${field} min-h-[80px] resize-none`} placeholder="Description" value={form.description} onChange={set("description")} data-testid="form-description" />
          <select className={field} value={form.status} onChange={set("status")} data-testid="form-status">
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="acquired">Acquired</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-ink-dim cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="form-featured" />
            Featured
          </label>
        </div>
        <button onClick={save} disabled={saving} data-testid="form-save" className="w-full mt-6 bg-gold text-bg font-mono text-xs uppercase tracking-[0.2em] py-3.5 hover:bg-gold-muted transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save Domain"}
        </button>
      </div>
    </div>
  );
}

function ResetPasswordModal({ target, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters.");
    setSaving(true);
    try {
      await adminResetUserPassword(target.user_id, pw);
      toast.success(`Password reset for ${target.email}.`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="reset-password-modal">
      <div className="bg-surface border border-gold/25 w-full max-w-md p-8">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-serif text-3xl text-ink">Reset password</h3>
          <button onClick={onClose} data-testid="close-reset-modal"><X className="text-ink-dim hover:text-ink" /></button>
        </div>
        <p className="text-ink-dim text-sm mb-6">Set a new password for <span className="text-gold font-mono">{target.email}</span>. Their active sessions will be signed out.</p>
        <input
          className={field}
          type="text"
          placeholder="New password (min 6 characters)"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          data-testid="reset-password-input"
          autoFocus
        />
        <button onClick={save} disabled={saving} data-testid="reset-password-save" className="w-full mt-6 bg-gold text-bg font-mono text-xs uppercase tracking-[0.2em] py-3.5 hover:bg-gold-muted transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Set new password"}
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading: authLoading, logout } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [tab, setTab] = useState("domains");
  const [stats, setStats] = useState({});
  const [domains, setDomains] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [glitter, setGlitter] = useState("sparkle");

  const load = useCallback(() => {
    adminStats().then(setStats).catch(() => {});
    getDomains().then(setDomains).catch(() => {});
    adminBookings().then(setBookings).catch(() => {});
    adminUsers().then(setUsers).catch(() => {});
    getSettings().then((s) => setGlitter(s.glitter_intensity || "sparkle")).catch(() => {});
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [load, isAdmin]);

  const changeGlitter = async (level) => {
    const prev = glitter;
    setGlitter(level);
    try { await adminUpdateSettings({ glitter_intensity: level }); toast.success(`Glitter set to "${level}".`); }
    catch { setGlitter(prev); toast.error("Could not update glitter."); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try { await adminDeleteDomain(id); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  const delUser = async (u) => {
    if (!window.confirm(`Permanently delete the account ${u.email}? This cannot be undone.`)) return;
    try { await adminDeleteUser(u.user_id); toast.success("Account deleted."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed."); }
  };

  const toggleBooking = async (b) => {
    const next = b.status === "processed" ? "new" : "processed";
    try { await adminUpdateBooking(b.id, next); load(); } catch { /* noop */ }
  };

  const delBooking = async (b) => {
    if (!window.confirm(`Delete the enquiry from ${b.full_name || b.email}?`)) return;
    try { await adminDeleteBooking(b.id); toast.success("Enquiry deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  const statCards = [
    { label: "Total Domains", value: stats.total_domains ?? "—" },
    { label: "Available", value: stats.available ?? "—" },
    { label: "Enquiries", value: stats.bookings ?? "—" },
    { label: "New Enquiries", value: stats.new_bookings ?? "—" },
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 size={28} className="animate-spin text-gold" />
      </div>
    );
  }
  if (!isAdmin) return <AdminGate />;

  return (
    <div className="min-h-screen bg-bg grain" data-testid="admin-page">
      <header className="border-b border-line bg-[var(--nav-bg)] backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rotate-45 bg-gold" />
            <span className="font-mono text-lg text-ink">elite-domains<span className="text-gold">.com</span></span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/30 px-2 py-1 ml-2">Admin</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" data-testid="admin-back" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink-dim hover:text-ink transition-colors">
              <ArrowLeft size={14} /> Site
            </Link>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink-dim hover:text-ink transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {statCards.map((s) => (
            <div key={s.label} className="bg-surface border border-line p-6" data-testid={`stat-${s.label}`}>
              <div className="font-serif text-5xl text-gold mb-2">{s.value}</div>
              <div className="overline">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 mb-8 bg-surface border border-line p-5" data-testid="glitter-control">
          <div>
            <div className="overline mb-1">Card glitter</div>
            <div className="text-ink-dim text-xs">Controls the shimmer intensity of every domain card in the collection.</div>
          </div>
          <div className="flex gap-2 sm:ml-auto">
            {["subtle", "sparkle", "max"].map((lvl) => (
              <button
                key={lvl}
                onClick={() => changeGlitter(lvl)}
                data-testid={`glitter-${lvl}`}
                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 rounded-full border transition-colors ${
                  glitter === lvl ? "bg-gold text-bg border-gold font-bold" : "text-ink-dim border-white/15 hover:border-gold/50 hover:text-ink"
                }`}
              >
                {lvl}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-6 border-b border-line mb-8">
          {["domains", "bookings", "users"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`font-mono text-xs uppercase tracking-[0.2em] pb-3 border-b-2 transition-colors ${tab === t ? "border-gold text-ink" : "border-transparent text-ink-dim hover:text-ink"}`}>
              {t} {t === "bookings" && `(${bookings.length})`}{t === "users" && `(${users.length})`}
            </button>
          ))}
        </div>

        {tab === "domains" && (
          <>
            <div className="flex justify-end mb-4">
              <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-domain-btn"
                className="flex items-center gap-2 bg-gold text-bg font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-gold-muted transition-colors">
                <Plus size={14} /> Add Domain
              </button>
            </div>
            <div className="border border-line overflow-x-auto">
              <table className="w-full text-left" data-testid="domains-table">
                <thead className="bg-surface-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ink-dim">
                  <tr>
                    <th className="p-4">Domain</th><th className="p-4">Category</th>
                    <th className="p-4">Price</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="font-mono text-sm">
                  {domains.map((d) => (
                    <tr key={d.id} className="border-t border-line hover:bg-surface transition-colors" data-testid={`row-${d.name}`}>
                      <td className="p-4 text-ink">{d.name}{d.featured && <span className="ml-2 text-gold text-[10px]">★</span>}</td>
                      <td className="p-4 text-ink-dim">{d.category}</td>
                      <td className="p-4 text-gold">{d.price}</td>
                      <td className="p-4"><span className="text-[10px] uppercase tracking-wider text-ink-dim">{d.status}</span></td>
                      <td className="p-4 text-right">
                        <div className="flex gap-3 justify-end">
                          <button onClick={() => { setEditing(d); setShowForm(true); }} data-testid={`edit-${d.name}`} className="text-ink-dim hover:text-gold transition-colors"><Pencil size={15} /></button>
                          <button onClick={() => del(d.id, d.name)} data-testid={`delete-${d.name}`} className="text-ink-dim hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "bookings" && (
          <div className="space-y-3" data-testid="bookings-list">
            {bookings.length === 0 && <div className="text-ink-dim font-mono text-sm py-12 text-center">No enquiries yet.</div>}
            {bookings.map((b) => (
              <div key={b.id} className="bg-surface border border-line p-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`booking-${b.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-mono text-gold text-lg">{b.domain_name}</span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-dim">{new Date(b.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="text-ink text-sm">{b.full_name} · <span className="text-ink-dim">{b.email}</span>{b.phone && <span className="text-ink-dim"> · {b.phone}</span>}</div>
                  {b.message && <p className="text-ink-dim text-sm mt-2 italic font-serif">&ldquo;{b.message}&rdquo;</p>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => toggleBooking(b)} data-testid={`booking-status-${b.id}`}
                    className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${b.status === "processed" ? "text-ink-dim border-line hover:border-gold/40" : "text-gold border-gold/40 hover:bg-gold/5"}`}>
                    {b.status === "processed" ? "Processed" : "Not processed"}
                  </button>
                  <button onClick={() => delBooking(b)} data-testid={`booking-delete-${b.id}`} title="Delete enquiry"
                    className="text-ink-dim hover:text-red-400 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {tab === "users" && (
          <div className="border border-line overflow-x-auto" data-testid="users-panel">
            <table className="w-full text-left" data-testid="users-table">
              <thead className="bg-surface-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ink-dim">
                <tr>
                  <th className="p-4">Email</th><th className="p-4">Name</th>
                  <th className="p-4">Role</th><th className="p-4">Wallet</th>
                  <th className="p-4">Sign-in</th><th className="p-4">Joined</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="font-mono text-sm">
                {users.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-ink-dim">No users yet.</td></tr>
                )}
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-line hover:bg-surface transition-colors" data-testid={`user-row-${u.email}`}>
                    <td className="p-4 text-ink">{u.email}</td>
                    <td className="p-4 text-ink-dim">{u.name || "—"}</td>
                    <td className="p-4">
                      <span className={`text-[10px] uppercase tracking-wider ${u.role === "admin" ? "text-gold" : "text-ink-dim"}`}>{u.role}</span>
                    </td>
                    <td className="p-4 text-gold">{formatUSD(u.wallet_balance)}</td>
                    <td className="p-4 text-ink-dim text-[11px] uppercase tracking-wider">{u.auth_provider === "google" ? "Google" : "Email"}</td>
                    <td className="p-4 text-ink-dim">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="p-4 text-right">
                      <div className="flex gap-3 justify-end">
                        <button onClick={() => setResetTarget(u)} data-testid={`reset-user-${u.email}`} title="Reset password" className="text-ink-dim hover:text-gold transition-colors"><KeyRound size={15} /></button>
                        <button onClick={() => delUser(u)} data-testid={`delete-user-${u.email}`} title="Delete account" className="text-ink-dim hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && <DomainForm initial={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {resetTarget && <ResetPasswordModal target={resetTarget} onClose={() => setResetTarget(null)} onSaved={() => { setResetTarget(null); load(); }} />}
    </div>
  );
}
